import { z } from 'zod';
import type { SportsProfilePreview } from './profile.js';
import { SportNameSchema, type SportName } from './profile.js';
import { CancellationPolicySchema, type CancellationPolicy } from './cancellation-policy.js';
import type { EntityVerificationStatus, OwnerEntityVerificationState } from './verification.js';

/**
 * Trainer-profile types + Zod schemas.
 *
 * F13 shipped the minimal stub (headline/bio/yearsExperience/acceptingNewClients).
 * F33 EXTENDS it with marketplace fields: languages, per-sport specialties,
 * locations-served toggles, avatar/cover/gallery media, a per-trainer
 * cancellation-policy override, deactivation, and admin suspension. Reviews
 * stay OUT (F37 — empty-state placeholders only). F09 v2 absorbed the F33
 * cert-verification roll-up into the per-entity trainer tick.
 *
 * See plans/33_F33_trainer_profile_marketplace.md.
 */

// ─── Length bounds (mirror schema CHECK constraints) ─────────────────────────

export const TRAINER_PROFILE_HEADLINE_MIN = 1;
export const TRAINER_PROFILE_HEADLINE_MAX = 80;
export const TRAINER_PROFILE_BIO_MIN = 1;
export const TRAINER_PROFILE_BIO_MAX = 280;
export const TRAINER_PROFILE_YEARS_MIN = 0;
export const TRAINER_PROFILE_YEARS_MAX = 80;
export const TRAINER_PROFILE_OWN_LOCATION_MAX = 200;
export const TRAINER_PROFILE_MAX_LANGUAGES = 10;
export const TRAINER_PROFILE_MAX_GALLERY = 8;
export const TRAINER_SPECIALTY_LEVEL_MIN = 1.0;
export const TRAINER_SPECIALTY_LEVEL_MAX = 7.0;

// ─── Enums ───────────────────────────────────────────────────────────────────

/** Where a trainer session physically happens. Drives the F33 booking branch. */
export const TRAINER_LOCATION_KIND_VALUES = [
  'affiliated_venue',
  'client_venue',
  'trainer_own_location',
] as const;
export type TrainerLocationKind = (typeof TRAINER_LOCATION_KIND_VALUES)[number];
export const TrainerLocationKindSchema = z.enum(TRAINER_LOCATION_KIND_VALUES);

/** ISO 639-1 (2-letter) language code. */
export const LanguageCodeSchema = z
  .string()
  .regex(/^[a-z]{2}$/, 'Expected a 2-letter ISO 639-1 language code');

// ─── Media variants ──────────────────────────────────────────────────────────

export type TrainerAvatarVariants = { readonly thumbUrl: string; readonly cardUrl: string };
export type TrainerCoverVariants = { readonly cardUrl: string; readonly heroUrl: string };
export type TrainerGalleryPhoto = { readonly url: string; readonly alt?: string };

// ─── Specialty (per trainer × sport) ─────────────────────────────────────────

export type TrainerSpecialty = {
  readonly sportName: SportName;
  readonly specialtyLevel: number | null;
  readonly isPrimary: boolean;
};

export const TrainerSpecialtyInputSchema = z
  .object({
    sportName: SportNameSchema,
    specialtyLevel: z
      .number()
      .min(TRAINER_SPECIALTY_LEVEL_MIN)
      .max(TRAINER_SPECIALTY_LEVEL_MAX)
      .nullable()
      .optional(),
    isPrimary: z.boolean().optional(),
  })
  .strict();
export type TrainerSpecialtyInput = z.infer<typeof TrainerSpecialtyInputSchema>;

/** Replace-all payload for `PUT /me/trainer-profile/specialties`. */
export const SetTrainerSpecialtiesInputSchema = z
  .object({
    specialties: z.array(TrainerSpecialtyInputSchema).max(41),
  })
  .strict()
  .refine(
    (v) => v.specialties.filter((s) => s.isPrimary).length <= 1,
    'At most one specialty may be primary',
  );
export type SetTrainerSpecialtiesInput = z.infer<typeof SetTrainerSpecialtiesInputSchema>;

// ─── Raw DB row shape (mirrors packages/db/src/schema.ts trainerProfiles) ────

export type TrainerProfile = {
  readonly userId: string;
  readonly headline: string | null;
  readonly bio: string | null;
  readonly yearsExperience: number | null;
  readonly acceptingNewClients: boolean;
  readonly languages: readonly string[];
  /**
   * F09 v2 (E3) — the trainer tick column `trainer_profiles.verification_status`
   * (`unverified | verified`). Present on every owner read; render the owner
   * state (in review, cooldown, …) from `verification` when it is present.
   */
  readonly verificationStatus: EntityVerificationStatus;
  /**
   * F09 v2 — the owner view of the trainer tick (E2/E14), produced via the W1
   * `owner-state.ts` derivation. Optional on the bare row: the photo-finalize
   * and gallery-delete responses return the profile row without it. Every
   * `OwnTrainerProfile` carries it (W3 T3.2).
   */
  readonly verification?: OwnerEntityVerificationState;
  readonly servesAffiliatedVenue: boolean;
  readonly servesClientVenue: boolean;
  readonly servesTrainerOwnLocation: boolean;
  readonly ownLocationText: string | null;
  readonly ownLocationLat: number | null;
  readonly ownLocationLng: number | null;
  readonly avatarUrl: string | null;
  readonly avatarVariants: TrainerAvatarVariants | null;
  readonly coverUrl: string | null;
  readonly coverVariants: TrainerCoverVariants | null;
  readonly gallery: readonly TrainerGalleryPhoto[];
  readonly cancellationPolicy: CancellationPolicy | null;
  readonly onboardingCompletedAt: string | null;
  readonly deactivatedAt: string | null;
  readonly suspendedAt: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * A trainer's own full profile (`GET /me/trainer-profile`, the PATCH / specialties
 * / onboarding-complete responses) — adds specialties and always carries the F09
 * v2 owner state of the trainer tick.
 */
export type OwnTrainerProfile = TrainerProfile & {
  readonly specialties: readonly TrainerSpecialty[];
  readonly verification: OwnerEntityVerificationState;
};

/**
 * Public-facing projection consumed by `GET /public/trainers/:userId` + the
 * affiliated-trainers section on `GET /public/venues/:slugOrId`. F33 extends
 * F13's shape with marketplace fields. Reviews are F37 — NOT present here.
 */
export type PublicTrainerProfile = {
  readonly userId: string;
  readonly displayName: string;
  /**
   * F09 v2 (E32) — the trainer tick: `trainer_profiles.verification_status =
   * 'verified'`. The ONLY field the tick renders from. (F09 v2 removed the
   * public `verificationStatus` status value — public shapes carry booleans.)
   */
  readonly verified: boolean;
  /** Display-only "since" date (`trainer_profiles.verified_at`); null when unverified. */
  readonly verifiedAt: string | null;
  readonly headline: string | null;
  readonly bio: string | null;
  readonly yearsExperience: number | null;
  readonly acceptingNewClients: boolean;
  readonly languages: readonly string[];
  readonly specialties: readonly TrainerSpecialty[];
  readonly servesAffiliatedVenue: boolean;
  readonly servesClientVenue: boolean;
  readonly servesTrainerOwnLocation: boolean;
  readonly avatarUrl: string | null;
  readonly avatarVariants: TrainerAvatarVariants | null;
  readonly coverUrl: string | null;
  readonly coverVariants: TrainerCoverVariants | null;
  readonly gallery: readonly TrainerGalleryPhoto[];
  /** Same shape as F08 `SportsProfilePreview.sports`. */
  readonly sports: SportsProfilePreview['sports'];
};

// ─── Zod schemas ─────────────────────────────────────────────────────────────

/**
 * Partial-update payload for `PATCH /me/trainer-profile`. Photo uploads
 * (avatar/cover/gallery) + specialties go through dedicated endpoints, NOT this
 * PATCH. `servesAffiliatedVenue` is always true (D10) so it's not editable here.
 */
export const UpdateTrainerProfileInputSchema = z
  .object({
    headline: z
      .string()
      .min(TRAINER_PROFILE_HEADLINE_MIN)
      .max(TRAINER_PROFILE_HEADLINE_MAX)
      .nullable()
      .optional(),
    bio: z.string().min(TRAINER_PROFILE_BIO_MIN).max(TRAINER_PROFILE_BIO_MAX).nullable().optional(),
    yearsExperience: z
      .number()
      .int()
      .min(TRAINER_PROFILE_YEARS_MIN)
      .max(TRAINER_PROFILE_YEARS_MAX)
      .nullable()
      .optional(),
    acceptingNewClients: z.boolean().optional(),
    languages: z.array(LanguageCodeSchema).max(TRAINER_PROFILE_MAX_LANGUAGES).optional(),
    servesClientVenue: z.boolean().optional(),
    servesTrainerOwnLocation: z.boolean().optional(),
    ownLocationText: z.string().max(TRAINER_PROFILE_OWN_LOCATION_MAX).nullable().optional(),
    ownLocationLat: z.number().min(-90).max(90).nullable().optional(),
    ownLocationLng: z.number().min(-180).max(180).nullable().optional(),
    cancellationPolicy: CancellationPolicySchema.nullable().optional(),
    /** Set/clear the self-deactivation flag (D11). */
    deactivated: z.boolean().optional(),
  })
  .strict();
export type UpdateTrainerProfileInput = z.infer<typeof UpdateTrainerProfileInputSchema>;
